import { Controller } from '@hotwired/stimulus';
import { useModal } from '../js/stimulus_use/use-modal';

const pushEvent = (action, label) => {
    dataLayer.push({
        event: 'dataLayer.gaEvent',
        gaEvent: { category: 'Account', action, label },
    });
};

/*
 * DDT-4157: the go-online pop-up (#onlineNowModal). It only shows the number and hours
 * the lawyer commits to and confirms the change; the status itself is saved by the
 * header toggle code in app.js. Opened by:
 *   - online-status-link (profile card button), through the outlet;
 *   - the header toggle, and the service page banner that goes through it, with the
 *     document event online-consultation:open-popup { source }.
 * It reports back with online-consultation:confirm { goOnline, source }, and follows the status
 * through online-status:changed { isOnline }, both on document.
 */
/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static values = {
        online: Boolean,
        activateLabel: String,
        deactivateLabel: String,
        reactivateUrl: String,
    };

    static targets = ['confirmButton', 'summary', 'suspendedNotice'];

    connect() {
        useModal(this);
    }

    // Called by online-status-link, and on online-consultation:open-popup.
    openFrom(source) {
        this.source = source;
        pushEvent('onlineconsult_popup_view', source);
        this.open();
    }

    openRequested(event) {
        this.openFrom(event.detail.source);
    }

    // The pop-up always confirms the direction the lawyer is not currently in.
    confirm() {
        this.close();
        document.dispatchEvent(new CustomEvent('online-consultation:confirm', {
            detail: { goOnline: !this.onlineValue, source: this.source },
        }));
    }

    statusChanged(event) {
        this.onlineValue = event.detail.isOnline;

        // Going online by hand lifts the schedule suspension (server side too).
        if (event.detail.isOnline) {
            this.scheduleResumed();
        }
    }

    // « Réactiver mes horaires » under the hours of a suspended schedule.
    async reactivateSchedule(event) {
        event.preventDefault();

        try {
            const response = await fetch(this.reactivateUrlValue, {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            const result = await response.json();

            if (!result.status) {
                addAlert('danger', translations['profile.An unknown error occurred.']);

                return;
            }

            pushEvent('onlineconsult_schedule_resume', '');
            this.summaryTarget.innerHTML = result.summary_html;
            document.dispatchEvent(new CustomEvent('online-consultation:schedule-resumed'));
        } catch (error) {
            addAlert('danger', translations['profile.An unknown error occurred.']);
        }
    }

    // online-consultation:schedule-resumed@document: from here, the page or a schedule save.
    scheduleResumed() {
        this.suspendedNoticeTargets.forEach((notice) => notice.remove());
    }

    onlineValueChanged(isOnline) {
        if (!this.hasConfirmButtonTarget) {
            return;
        }

        this.confirmButtonTarget.classList.toggle('calendar-sync-btn--danger', isOnline);
        this.confirmButtonTarget.classList.toggle('calendar-sync-btn--green', !isOnline);
        this.confirmButtonTarget.textContent = isOnline ? this.deactivateLabelValue : this.activateLabelValue;
    }

    // The links lead to the service page sections. Figma: on the page itself the link
    // closes the pop-up and goes back to the settings, so the jump is done by hand once
    // the pop-up has closed (the open modal locks the page scroll). Elsewhere the browser
    // just follows the link.
    followLink(event) {
        const link = event.currentTarget;

        pushEvent('onlineconsult_popup_settings_click', link.dataset.onlineConsultPopupLink);

        const section = document.querySelector(link.hash);

        if (!section) {
            return;
        }

        event.preventDefault();

        this.element.addEventListener('hidden.bs.modal', () => {
            section.scrollIntoView({ behavior: 'smooth' });
            window.history.replaceState(null, '', link.hash);
        }, { once: true });

        this.close();
    }
}
